import React, { useState, useEffect, useCallback } from "react";
import axios from '../../api/apiClient';
import {
  CreateTableNew as CreateTable,
  SearchBar,
  Button,
  GeneralMessage,
  useGeneralMessage
} from '../../common/APICommon';
import { Input, Checkbox } from '../../common/Input';
import { Select } from "../../common/Select";
import { token, domain } from '../Security';
import { LoadingSpinner } from '../../common/Loading';
import { fetchOTTransactionTypesEnum } from "../../api/fetch";
import { transformEnumData } from "../../utils/textUtils";

// icons
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from '@mui/icons-material/Edit';

function PayrollRateConfig({ user, isHideSideMenu }) {
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  const headers = [
    "ID",
    "Transaction Type",
    "Description",
    "Day Multiplier",
    "OT Multiplier",
    "ND Multiplier",
    "Status",
    "Actions"
  ];

  const accessors = [
    "id",
    "transactionTypeName",
    "description",
    "dayMultiplier",
    "otMultiplier",
    "nightDiffMultiplier",
    "isActive",
    "actions"
  ];

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [data, setData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);

  // States for Dropdowns
  const [transactionTypeOptions, setTransactionTypeOptions] = useState([]);
  const [nightDiffOptions, setNightDiffOptions] = useState([]);
  const [enumsLoading, setEnumsLoading] = useState(false);

  // Form state
  const [form, setForm] = useState({
    transactionType: "",
    description: "",
    dayMultiplier: "",
    otMultiplier: "",
    nightDifferentialId: "", // Matches the nullable int in backend
    isActive: true,
  });

  const [filterData, setFilterData] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  const apiUrl = domain + "/api/PayrollRateConfig";
  const ndApiUrl = domain + "/api/NightDifferential";

  const resetLocalVariables = () => {
    setForm({
      transactionType: "",
      description: "",
      dayMultiplier: "",
      otMultiplier: "",
      nightDifferentialId: "",
      isActive: true,
    });
    setSelectedData(null);
  };

  // Fetch Enums and Night Differential Options
  const fetchDependencies = useCallback(async () => {
    try {
      setEnumsLoading(true);
      
      const transactionData = await fetchOTTransactionTypesEnum();
      setTransactionTypeOptions(transformEnumData(transactionData));

      const ndResponse = await axios.get(ndApiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Transform ND data: value is the ID, label shows the name and %
      const ndMapped = ndResponse.data
        .filter(item => item.isActive)
        .map(item => ({
          value: item.id.toString(), 
          label: `${item.description} (${item.percentage}%)`
        }));
      setNightDiffOptions(ndMapped);

    } catch (error) {
      console.error("Error fetching dependencies:", error);
    } finally {
      setEnumsLoading(false);
    }
  }, [ndApiUrl]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setData(response.data);
      setFilterData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchDependencies();
    fetchData();
  }, [fetchDependencies, fetchData]);

  const handleFilter = (value) => {
    setCurrentPage(1);
    const lowercaseValue = value.toLowerCase();
    const res = filterData.filter((f) =>
      f?.transactionTypeName?.toLowerCase().includes(lowercaseValue) ||
      f?.description?.toLowerCase().includes(lowercaseValue)
    );
    setData(res);
  };

  // Helper to prepare data for API (handles nullable decimals and ints)
  const preparePayload = () => {
    return {
      transactionType: parseInt(form.transactionType),
      description: form.description,
      // If string is empty, send null for the nullable decimal/int fields
      dayMultiplier: form.dayMultiplier === "" ? null : parseFloat(form.dayMultiplier),
      otMultiplier: form.otMultiplier === "" ? null : parseFloat(form.otMultiplier),
      nightDifferentialId: form.nightDifferentialId === "" ? null : parseInt(form.nightDifferentialId),
      isActive: form.isActive,
    };
  };

  const handleAddSubmit = async (event) => {
    event.preventDefault();

    if (!form.transactionType || !form.description) {
      showGeneralMessage("Please fill in required fields", false);
      return;
    }

    const apiData = preparePayload();

    try {
      setLoadingRequest(true);
      await axios.post(apiUrl, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      await fetchData();
      showGeneralMessage("Payroll Config created successfully!", true);
      closeAddHandleClick();
    } catch (error) {
      console.error('Error creating data:', error);
      showGeneralMessage(error.response?.data?.message || "Failed to create config", false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const handleUpdateSubmit = async (event) => {
    event.preventDefault();
    if (!selectedData?.id) return;

    const apiData = preparePayload();

    try {
      setLoadingRequest(true);
      await axios.put(`${apiUrl}/${selectedData.id}`, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      await fetchData();
      showGeneralMessage("Payroll Config updated successfully!", true);
      closeEditHandleClick();
    } catch (error) {
      console.error('Error updating data:', error);
      showGeneralMessage("Failed to update config", false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const editHandleClick = (data) => {
    setSelectedData(data);
    setForm({
      transactionType: data.transactionType?.toString() || "",
      description: data.description || "",
      // Handle nulls from database by converting back to empty string for inputs
      dayMultiplier: data.dayMultiplier != null ? data.dayMultiplier.toString() : "",
      otMultiplier: data.otMultiplier != null ? data.otMultiplier.toString() : "",
      nightDifferentialId: data.nightDifferentialId != null ? data.nightDifferentialId.toString() : "",
      isActive: data.isActive,
    });
    setShowEditForm(true);
  };

  const openAddHandleClick = () => setShowAddForm(true);

  const closeAddHandleClick = () => {
    setShowAddForm(false);
    resetLocalVariables();
  };

  const closeEditHandleClick = () => {
    setShowEditForm(false);
    resetLocalVariables();
  };

  const actions = [
    {
      name: "Edit",
      tooltip: "edit",
      icon: <EditIcon fontSize="small" />,
      className: "bg-green-500 hover:bg-green-600 flex items-center",
      function: editHandleClick,
      visible: true,
    },
  ];

  const customCellRenderer = (row, accessor) => {
    if (['dayMultiplier', 'otMultiplier', 'nightDiffMultiplier'].includes(accessor)) {
      const value = row[accessor];
      
      // Handle null/empty values for nullable fields
      if (value === null || value === undefined || value === "") {
        return <span className="text-gray-400">-</span>;
      }

      // Math: 1.3 -> 130%, 0.1 -> 10%
      const percentage = (parseFloat(value) * 100).toFixed(0);
      return <span className="font-medium text-blue-600">{percentage}%</span>;
    }
    
    // Returning null allows the table to fall back to default getNestedValue logic
    return null;
  };

  return (
    <div>
      <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
        <div className="LeftSpace"></div>
        <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
          <div className="MainteInfo shadow-xl">
            <div className="mainteHeader">
              <div className='sub-title row'>
                <span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Dev Mode >"} </span>
                <span style={{ borderBottom: "1px solid black" }}>{"Payroll Config"}</span>
              </div>
              <h3>Payroll Rate Configuration</h3>
            </div>
            <div className='p-4 space-y-4'>
              <SearchBar handleFilter={handleFilter} />

              <Button
                icon={<AddIcon />}
                name={"Add Configuration"}
                className={"bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"}
                onClick={openAddHandleClick}
              />

              <CreateTable
                tableName={"PayrollRateConfig"}
                headers={headers}
                accessors={accessors}
                data={data}
                actions={actions}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                itemsPerPage={10}
                totalItems={data?.length}
                customCellRenderer={customCellRenderer}
              />
            </div>
          </div>
        </div>
      </div>

      <AddEditForm
        isOpen={showAddForm}
        onClose={closeAddHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleAddSubmit}
        modalName={"Add Payroll Config"}
        transactionTypeOptions={transactionTypeOptions}
        nightDiffOptions={nightDiffOptions}
        enumsLoading={enumsLoading}
      />

      <AddEditForm
        isOpen={showEditForm}
        onClose={closeEditHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleUpdateSubmit}
        modalName={"Update Payroll Config"}
        isEdit={true}
        transactionTypeOptions={transactionTypeOptions}
        nightDiffOptions={nightDiffOptions}
        enumsLoading={enumsLoading}
      />

      <GeneralMessage form={showMessage} message={message} isSuccess={isSuccess} />
      <LoadingSpinner loading={loadingRequest || loading || enumsLoading} />
    </div>
  );
}

function AddEditForm({
  isOpen,
  modalName,
  onClose,
  form,
  setForm,
  handleSubmit,
  isEdit = false,
  transactionTypeOptions,
  nightDiffOptions,
  enumsLoading
}) {

  const handleInputChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto">
      <div className="bg-gray-50 w-full md:w-[90%] lg:w-[50%] rounded-lg shadow-xl h-auto max-h-[90vh] flex flex-col relative">

        <div className="flex justify-between items-center px-6 py-2 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-lg shadow-sm">
          <h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
            <span className="text-xl text-gray-600">{modalName}</span>
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition-colors p-1 bg-transparent border-none cursor-pointer">
            <CloseIcon fontSize="medium" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-grow space-y-4">
          <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
            <div className="py-1 border-b border-gray-300">
              <h3 className="font-medium text-gray-700">General Information</h3>
            </div>
            <div className="grid grid-cols-1 gap-4 pb-3 pt-3">
              <Select
                label="Transaction Type"
                value={form.transactionType}
                onChange={(e) => handleInputChange('transactionType', e.target.value)}
                options={transactionTypeOptions}
                placeholder="Select transaction type"
                isRequired={true}
              />
              <Input
                label="Description"
                value={form.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="e.g. Regular Overtime"
                isRequired={true}
              />
            </div>
          </div>

          <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
            <div className="py-1 border-b border-gray-300">
              <h3 className="font-medium text-gray-700">Rate Multipliers</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3">
              <Input
                label="Day Multiplier"
                value={form.dayMultiplier}
                onChange={(e) => handleInputChange('dayMultiplier', e.target.value)}
                placeholder="Leave blank if not applicable"
                type="number"
                step="0.01"
                tooltip="Multiplier for the first 8 hours (Nullable)"
              />
              <Input
                label="OT Multiplier"
                value={form.otMultiplier}
                onChange={(e) => handleInputChange('otMultiplier', e.target.value)}
                placeholder="Leave blank if not applicable"
                type="number"
                step="0.01"
                tooltip="Multiplier for hours exceeding 8 hours (Nullable)"
              />
            </div>
            <div className="pb-3 pt-3">
              <Select
                label="Night Differential Rate"
                value={form.nightDifferentialId}
                onChange={(e) => handleInputChange('nightDifferentialId', e.target.value)}
                options={nightDiffOptions}
                placeholder={enumsLoading ? "Loading rates..." : "Select ND Rate (Nullable)"}
                tooltip="Select the Night Diff ID from maintenance"
              />
            </div>
          </div>

          <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
            <div className="py-1 border-b border-gray-300">
              <h3 className="font-medium text-gray-700">Status</h3>
            </div>
            <div className="pb-3 pt-3">
              <Checkbox
                label="Active"
                checked={form.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2 p-5 border-t border-gray-200 bg-white shadow-sm">
          <button onClick={onClose} className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded border-none cursor-pointer transition-colors">
            Cancel
          </button>
          <button onClick={handleSubmit} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded border-none cursor-pointer transition-colors">
            {isEdit ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PayrollRateConfig;